import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Spinner } from 'reactstrap';
import Select from '../../Components/Common/ThemedSelect';
import api from '../../api';

/**
 * Round REMIND-1 (2026-10-03) — hand over the open work of one person.
 *
 * Open work = the EPO approvals waiting on them and the active people who report to them.
 * HR picks who takes over (suggested: the person's own supervisor); the EPO steps move to that
 * person (they get the usual "awaiting your approval" notice) and the reports get them as
 * supervisor, so their waiting FTO / COA requests follow. Used by Employees → Hand over work and
 * right after Manage Employee sets someone inactive. Server: HandoverController / HandoverService.
 */
const money = (v) =>
    v == null ? '' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const HandoverDialog = ({ empId, isOpen, onClose, onDone, intro }) => {
    const [detail, setDetail] = useState(null);       // null = loading, undefined = failed
    const [people, setPeople] = useState([]);
    const [taker, setTaker] = useState(null);
    const [moveEpos, setMoveEpos] = useState(true);
    const [moveReports, setMoveReports] = useState(true);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!isOpen || !empId) return;
        let gone = false;
        setDetail(null); setTaker(null); setReason(''); setError(''); setResult(null); setBusy(false);
        api.get(`hr/handover/${encodeURIComponent(empId)}`)
            .then((r) => {
                if (gone) return;
                const d = r.data;
                setDetail(d);
                setMoveEpos((d?.epos?.length || 0) > 0);
                setMoveReports((d?.directReports?.length || 0) > 0);
                if (d?.person?.suggestedEmpId) {
                    setTaker({ value: d.person.suggestedEmpId, label: `${d.person.suggestedName} (${d.person.suggestedEmpId})` });
                }
            })
            .catch(() => { if (!gone) setDetail(undefined); });
        api.get('registration/supervisors', { params: { all: true } })
            .then((r) => { if (!gone) setPeople(Array.isArray(r.data) ? r.data : []); })
            .catch(() => { if (!gone) setPeople([]); });
        return () => { gone = true; };
    }, [isOpen, empId]);

    const options = useMemo(() => people
        .map((p) => ({ id: String(p.empID ?? p.empId ?? '').trim(), name: p.name }))
        .filter((p) => p.id && p.id !== String(empId).trim())
        .map((p) => ({ value: p.id, label: `${p.name} (${p.id})` })), [people, empId]);

    const name = detail?.person?.name || empId;
    const epos = detail?.epos || [];
    const reports = detail?.directReports || [];
    const nothing = detail && epos.length === 0 && reports.length === 0;

    const close = () => {
        if (busy) return;
        if (result && onDone) onDone(result);
        onClose();
    };

    const submit = async () => {
        if (!taker) { setError('Choose who takes over.'); return; }
        if (!moveEpos && !moveReports) { setError('Choose what to hand over.'); return; }
        setBusy(true); setError('');
        try {
            const r = await api.post(`hr/handover/${encodeURIComponent(empId)}`, {
                toEmpId: taker.value,
                moveEpoApprovals: moveEpos && epos.length > 0,
                moveDirectReports: moveReports && reports.length > 0,
                reason: reason.trim() || null,
            });
            setResult({ ...r.data, takerLabel: taker.label });
        } catch (e) {
            setError(e?.response?.data?.message || 'The hand over did not go through. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={close} centered scrollable size="lg">
            <ModalHeader toggle={close}>Hand over open work</ModalHeader>
            <ModalBody>
                {intro && <p className="mb-2">{intro}</p>}

                {detail === null && (
                    <div className="d-flex align-items-center gap-2 text-muted py-3"><Spinner size="sm" /> Looking for open work…</div>
                )}
                {detail === undefined && (
                    <div className="alert alert-warning mb-0">The open work of {empId} could not be loaded.</div>
                )}

                {result && (
                    <div>
                        <div className="alert alert-success">
                            Done. {plural(result.eposMoved, 'EPO approval', 'EPO approvals')} and{' '}
                            {plural(result.reportsMoved, 'person', 'people')} now go to {result.takerLabel}.
                        </div>
                        {(result.notes || []).map((n, i) => <p key={i} className="text-muted mb-1">{n}</p>)}
                    </div>
                )}

                {detail && !result && nothing && (
                    <p className="text-muted mb-0">Nothing is waiting on {name}.</p>
                )}

                {detail && !result && !nothing && (
                    <div>
                        <p className="text-muted">Give the things that wait on {name} to someone who is still here.</p>

                        {epos.length > 0 && (
                            <div className="mb-3">
                                <div className="form-check mb-1">
                                    <Input type="checkbox" className="form-check-input" id="ho-epos"
                                        checked={moveEpos} onChange={(e) => setMoveEpos(e.target.checked)} />
                                    <Label className="form-check-label fw-semibold" htmlFor="ho-epos">
                                        Move {plural(epos.length, 'EPO approval', 'EPO approvals')} waiting on {name}
                                    </Label>
                                </div>
                                <ul className="list-unstyled small mb-0 ps-4" style={{ maxHeight: 160, overflowY: 'auto' }}>
                                    {epos.map((e) => (
                                        <li key={e.epoNumber}>
                                            EPO {e.epoNumber} from {e.requester} {money(e.amount)}
                                            <span className="text-muted"> · {e.currentStep ? 'waiting now' : 'a later step'}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {reports.length > 0 && (
                            <div className="mb-3">
                                <div className="form-check mb-1">
                                    <Input type="checkbox" className="form-check-input" id="ho-reports"
                                        checked={moveReports} onChange={(e) => setMoveReports(e.target.checked)} />
                                    <Label className="form-check-label fw-semibold" htmlFor="ho-reports">
                                        Give the {plural(reports.length, 'person', 'people')} who report to {name} a new supervisor
                                    </Label>
                                </div>
                                <div className="small ps-4">{reports.map((r) => r.name).join(', ')}</div>
                                <div className="small text-muted ps-4 mt-1">
                                    Their waiting FTO and COA requests go to the new supervisor.
                                    Until go-live, the copy from the old system can put the old supervisor back,
                                    so change it in the old system too.
                                </div>
                            </div>
                        )}

                        <div className="mb-3">
                            <Label className="form-label fw-semibold" htmlFor="ho-taker">Who takes over?</Label>
                            <Select
                                inputId="ho-taker"
                                options={options}
                                value={taker}
                                onChange={setTaker}
                                placeholder="Search by name or number"
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
                            />
                            {detail?.person?.suggestedEmpId && (
                                <div className="small text-muted mt-1">Suggested: {name}'s own supervisor.</div>
                            )}
                        </div>

                        <div className="mb-1">
                            <Label className="form-label" htmlFor="ho-reason">Note (optional)</Label>
                            <Input id="ho-reason" type="text" maxLength={200} value={reason}
                                onChange={(e) => setReason(e.target.value)} placeholder="For example: left the company" />
                        </div>
                        {error && <div className="text-danger small mt-2" role="alert">{error}</div>}
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                {result || nothing || detail === undefined ? (
                    <Button color="primary" onClick={close}>Close</Button>
                ) : (
                    <>
                        <Button color="light" onClick={close} disabled={busy}>Not now</Button>
                        <Button color="primary" onClick={submit} disabled={busy || !detail}>
                            {busy ? <><Spinner size="sm" className="me-1" />Handing over…</> : 'Hand over'}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default HandoverDialog;
